import { useEffect, useState, useRef } from 'react';
import styles from './styles/CartSidebar.module.css';
import { X, ChevronRight } from 'lucide-react';
import { CartItem } from './cartItem';
import { calculateUnitPrice } from './utils/calcPrice';
import { getCurrencySymbol } from './utils/calcPrice';
import { catalogApi } from '../../services/CatalogService';
import { showToast } from '../../components/Toast';
import { useIsMobile } from '../../hooks/useIsMobile';
import CatalogLoader from '../../components/Loaders/catalogLoader';
import {
  itemsAtPriceText,
  emptyCartText,
  summaryText,
  totalLabel,
  sendOrderBtn,
  emailText,
  submitText,
  submitTextExtra,
  confirmSendText,
  cancelText,
  thankYouText,
  contactSoonText
} from '../../helpers/currency_helper';
import SweetAlert from 'react-bootstrap-sweetalert';

export const CartSidebar = ({ order, items, setItems, onClose, isOpen }) => {
  const isMobile = useIsMobile();
  const [isSummaryVisible, setIsSummaryVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  const summaryRef = useRef(null);
  const footerRef = useRef(null);

  const [createClientOrder] = catalogApi.useCompleteOrderMutation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSummaryVisible(entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0.1
      }
    );

    if (summaryRef.current) {
      observer.observe(summaryRef.current);
    }

    return () => {
      if (summaryRef.current) {
        observer.unobserve(summaryRef.current);
      }
    };
  }, []);

  const handleQuantityChange = (itemId, newQuantity) => {
    setItems((prevItems) => prevItems.map((item) => (item._id === itemId ? { ...item, quantity: newQuantity } : item)));
  };

  const handleRemoveItem = (itemId) => {
    setItems((prevItems) => prevItems.filter((item) => item._id !== itemId));
  };

  const handleCheckout = () => {
    if (!isSummaryVisible && footerRef.current) {
      footerRef.current.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    // Validation (example from before)
    if (!email.trim()) {
      setEmailError('Email is required.');
      return;
    }

    if (!isValidEmail(email)) {
      setEmailError('Please enter a valid email address.');
      return;
    }

    setEmailError('');
    setShowAlert(true);
  };

  const handleSubmit = async () => {
    let cart = [];
    items.forEach((item) => {
      const { unitPrice, totalPrice } = calculateUnitPrice(item);
      cart.push({
        _id: item._id,
        nomenclatureCode: item.nomenclatureCode,
        nomenclatureCode1C: item.nomenclatureCode1C,
        title: item.title,
        quantity: item.quantity,
        unitPrice,
        totalPrice
      });
    });

    try {
      setLoading(true); // 👈 Show spinner
      setOrderSubmitted(false);

      await createClientOrder([
        {
          email,
          _id: order[0]._id,
          manager_email: order[0].manager.workEmail,
          bitrixID: order[0].manager.bitrixID,
          manager_name: `${order[0].manager.fname} ${order[0].manager.sname}`,
          clientName: order[0].clientName,
          createdAt: order[0].createdAt,
          invoiceNo: order[0].invoiceNo,
          orderInfo: cart,
          notification: 'catalog_order_submit'
        }
      ]).unwrap();

      setOrderSubmitted(true); // 👈 Triggers success message
      showToast('✅ New Order is created');
      onClose();
    } catch (error) {
      setShowAlert(false); // 👈 Close alert if failed
      console.error('❌ Failed to create an order:', error);
      showToast('❌ Failed to create an order', 'error');
    } finally {
      setLoading(false); // 👈 Hide spinner
    }
  };

  const isValidEmail = (email) => {
    // Simple email regex
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const total = items.reduce((sum, item) => sum + calculateUnitPrice(item).totalPrice, 0).toFixed(2);
  return (
    <>
      {isOpen && (
        <button className={styles.sideToggleBtn} onClick={onClose}>
          <X className={styles.rotateOnHover} size={48} />
        </button>
      )}
      <div className={`${styles.sidebar} ${isOpen ? styles.open : styles.closed}`}>
        {isMobile && (
          <button className={styles.mobileCloseBtn} onClick={onClose}>
            <X size={24} />
          </button>
        )}
        {/* Title section */}
        <div className={styles.cartTitle}>
          <span>
            {items.length} {itemsAtPriceText[order[0].language]} {total}
            {getCurrencySymbol(items[0]?.country, items[0]?.price_r)}
          </span>
        </div>

        {/* Scrollable content: cart items */}
        <div className={styles.cartContent}>
          {items.length === 0 ? (
            <p className={styles.emptyText}>{emptyCartText[order[0].language]} </p>
          ) : (
            items.map((item) => <CartItem key={item._id} item={item} onQuantityChange={handleQuantityChange} onRemove={handleRemoveItem} />)
          )}
        </div>

        {/* Summary label */}
        <div className={styles.summaryTitle}>{summaryText[order[0].language]}</div>

        {/* Summary list */}
        <div ref={summaryRef} className={styles.itemSummary}>
          {items.map((item) => (
            <div key={item._id} className={styles.itemLine}>
              <span className={styles.title}>{item.title}</span>
              <div className={styles.qtyWrapper}>
                <span className={styles.itemQty}>x{item.quantity}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Sticky footer with total and button */}
        <div ref={footerRef} className={`${styles.footerBottom} ${isSummaryVisible ? styles.footerStatic : styles.footerSticky}`}>
          {isSummaryVisible ? (
            <>
              <div className={styles.emailInputWrapper}>
                <label htmlFor='email' className={styles.emailLabel}>
                  Email*
                </label>
                <input type='email' id='email' placeholder='you@example.com' value={email} onChange={(e) => setEmail(e.target.value)} className={styles.emailInput} />
                <span className={styles.emailRemark}>{emailText[order[0].language]}</span>
                {emailError && <span className={styles.error}>{emailError}</span>}
              </div>
              <div className={styles.total}>
                <span>{totalLabel[order[0].language]}</span>
                <span style={{ marginBottom: isSummaryVisible ? '16px' : '0px' }}>
                  {total} {getCurrencySymbol(items[0]?.country, items[0]?.price_r)}
                </span>
              </div>
            </>
          ) : (
            <span className={styles.totalPrice}>
              {total} {getCurrencySymbol(items[0]?.country, items[0]?.price_r)}
            </span>
          )}

          <button className={styles.submitBtn} onClick={handleCheckout}>
            {sendOrderBtn[order[0].language]}
            <ChevronRight size={18} className={styles.arrowIcon} />
          </button>
        </div>
      </div>
      {showAlert && (
        <SweetAlert
          showCancel={!orderSubmitted && !loading}
          showConfirm={!loading}
          confirmBtnText={loading ? '' : orderSubmitted ? 'OK' : `${confirmSendText[order[0].language]}`}
          cancelBtnText={!orderSubmitted && !loading ? `${cancelText[order[0].language]}` : undefined}
          confirmBtnStyle={{ backgroundColor: '#155c1f', color: 'white', borderRadius: '999px' }}
          cancelBtnStyle={{ backgroundColor: '#e0e0e0', color: 'black', borderRadius: '999px' }}
          title={loading ? `${submitText[order[0].language]}...` : orderSubmitted ? `${thankYouText[order[0].language]}` : `${submitText[order[0].language]}`}
          onConfirm={async () => {
            if (!orderSubmitted && !loading) {
              await handleSubmit(); // this shows loader and triggers final state
            } else {
              // Reset and close alert after success
              setOrderSubmitted(false);
              setShowAlert(false);
            }
          }}
          onCancel={() => {
            setOrderSubmitted(false); // 👈 Reset here too
            setShowAlert(false);
          }}
          type={orderSubmitted ? 'success' : 'warning'}
        >
          {loading ? (
            <div style={{ padding: '20px' }}>
              <div className='spinner-border text-success' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          ) : orderSubmitted ? (
            `${contactSoonText[order[0].language]}`
          ) : (
            `${submitTextExtra[order[0].language]}`
          )}
        </SweetAlert>
      )}
    </>
  );
};
